import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { parseGid, useProduct } from "@shopify/hydrogen-react";

import { useProductReviewsAggregate } from "@react/hooks/useProductReviewsAggregate";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  isMigratedBundle,
  isStandaloneBundleKit,
  isVariantFixedBundle,
} from "@react/utilities";
import {
  type ReviewItemBlock,
  type ProductHeroSettings,
} from "@root/tablescape/state";
import {
  formatProduct,
  formatProductVariant,
} from "@root/tablescape/utilities";
import { ReviewsAccordionItemView } from "@sections/ProductHero/ProductHeroAccordion/ReviewsAccordionItem/ReviewsAccordionItem.view";

type Props = {
  isOpen?: boolean;
  itemIndex?: number | undefined;
  onToggle?: (index: number) => void | undefined;
  rating: {
    textReadMoreReviews: string;
    textReviews: string;
  };
  reviews: ReviewItemBlock[];
};

const ReviewsAccordionItemBase = ({
  isOpen = false,
  itemIndex = undefined,
  onToggle = undefined,
  rating,
  reviews,
  ...props
}: Props) => {
  const { product, selectedVariant } = useProduct();

  const {
    settings: { theme },
  } = useSectionSettings<ProductHeroSettings>();

  const [isInternallyOpen, setIsInternallyOpen] = useState<boolean>(isOpen);
  useEffect(() => setIsInternallyOpen(isOpen), [isOpen]);

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const toggleHandler = useCallback(() => {
    if (onToggle && itemIndex !== undefined) {
      onToggle(itemIndex);
    } else {
      setIsInternallyOpen(!isInternallyOpen);
    }
  }, [isInternallyOpen, itemIndex, onToggle]);

  const reviewProductIds = useMemo(() => {
    if (
      isVariantFixedBundle([formattedSelectedVariant]) ||
      isStandaloneBundleKit(formattedSelectedVariant)
    ) {
      return formattedSelectedVariant.variantFixedBundleComponents?.map(
        (variant) => parseGid(variant.product.id).id
      );
    } else if (isMigratedBundle(formattedProduct)) {
      return formattedProduct.productFixedBundleComponents?.map(
        (product) => parseGid(product.id).id
      );
    }
    return [parseGid(product.id).id];
  }, [formattedProduct, formattedSelectedVariant, product.id]);

  const { aggregateReviewCount } = useProductReviewsAggregate(
    reviewProductIds || []
  );

  const heading = useMemo(() => {
    const count =
      aggregateReviewCount && aggregateReviewCount.toLocaleString("en");
    return `${rating?.textReviews} ${count ? `(${count})` : ""}`;
  }, [aggregateReviewCount, rating?.textReviews]);

  const jumpToReviewsSection = useCallback(() => {
    const section = document.getElementById(
      `reviews-for-${parseGid(product.id).id}`
    );
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  }, [product.id]);

  const isReviewsSectionPresent = useMemo(() => {
    return !!document.getElementById(`reviews-for-${parseGid(product.id).id}`);
  }, [product.id]);

  return (
    <ReviewsAccordionItemView
      buttonText={rating.textReadMoreReviews}
      heading={heading}
      isOpen={isInternallyOpen}
      onClick={toggleHandler}
      reviews={reviews}
      reviewsSectionLinkHandler={jumpToReviewsSection}
      showReadMoreButton={isReviewsSectionPresent}
      theme={theme}
      {...props}
    />
  );
};

export const ReviewsAccordionItem = memo(ReviewsAccordionItemBase);
